import { useState, useRef, useEffect } from "react";
import { CalendarIcon } from "./icons";
import { MAX_TRIP_DAYS } from "./constants";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const getTodayISO = () => {
  const today = new Date();
  return today.toISOString().split("T")[0];
};

const toISO = (year, month, day) => {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

const addDays = (dateStr, days) => {
  const date = new Date(dateStr);
  date.setDate(date.getDate() + days);
  return date.toISOString().split("T")[0];
};

const generateCalendarDays = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDay = firstDay.getDay();

  const days = [];

  for (let i = 0; i < startingDay; i++) {
    days.push(null);
  }

  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  return days;
};

const DateRangePicker = ({ startDate, endDate, onStartDateChange, onEndDateChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const [currentMonth, setCurrentMonth] = useState(() => {
    const date = startDate ? new Date(startDate) : new Date();
    return { year: date.getFullYear(), month: date.getMonth() };
  });
  const [hoverDate, setHoverDate] = useState(null);
  const [selectionPhase, setSelectionPhase] = useState("start");
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  const CALENDAR_HEIGHT = 440;
  const CALENDAR_WIDTH = 340;

  const today = getTodayISO();

  useEffect(() => {
    if (startDate && !endDate) {
      setSelectionPhase("end");
    } else if (!startDate) {
      setSelectionPhase("start");
    }
  }, [startDate, endDate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setHoverDate(null);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const getDuration = () => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const nights = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    if (nights <= 0) return null;
    return { nights, days: nights + 1 };
  };

  const duration = getDuration();

  const prevMonth = () => {
    setCurrentMonth((prev) => {
      if (prev.month === 0) {
        return { year: prev.year - 1, month: 11 };
      }
      return { year: prev.year, month: prev.month - 1 };
    });
  };

  const nextMonth = () => {
    setCurrentMonth((prev) => {
      if (prev.month === 11) {
        return { year: prev.year + 1, month: 0 };
      }
      return { year: prev.year, month: prev.month + 1 };
    });
  };

  const isPastDate = (dateStr) => {
    return dateStr < today;
  };

  const exceedsMaxRange = (dateStr) => {
    if (!startDate || selectionPhase !== "end") return false;
    const maxDate = addDays(startDate, MAX_TRIP_DAYS - 1);
    return dateStr > maxDate;
  };

  const isBeforeStart = (dateStr) => {
    if (!startDate || selectionPhase !== "end") return false;
    return dateStr < startDate;
  };

  const getEffectiveEndDate = () => {
    if (selectionPhase === "end" && hoverDate && hoverDate >= startDate) {
      return hoverDate;
    }
    return endDate;
  };

  const isInRange = (dateStr) => {
    if (!startDate) return false;
    const effectiveEnd = getEffectiveEndDate();
    if (!effectiveEnd) return false;
    return dateStr > startDate && dateStr < effectiveEnd;
  };

  const isStartDate = (dateStr) => dateStr === startDate;
  const isEndDate = (dateStr) => {
    const effectiveEnd = getEffectiveEndDate();
    return dateStr === effectiveEnd;
  };

  const isHoverEnd = (dateStr) => {
    return selectionPhase === "end" && dateStr === hoverDate && hoverDate >= startDate;
  };

  const handleDayClick = (day) => {
    const dateStr = toISO(currentMonth.year, currentMonth.month, day);

    if (selectionPhase === "start") {
      onStartDateChange(dateStr);
      onEndDateChange("");
      setSelectionPhase("end");
    } else {
      if (dateStr < startDate) {
        onStartDateChange(dateStr);
        onEndDateChange("");
        setSelectionPhase("end");
      } else {
        onEndDateChange(dateStr);
        setSelectionPhase("start");
        setIsOpen(false);
        setHoverDate(null);
      }
    }
  };

  const handleDayHover = (day) => {
    if (day === null) {
      setHoverDate(null);
      return;
    }
    const dateStr = toISO(currentMonth.year, currentMonth.month, day);
    setHoverDate(dateStr);
  };

  const calendarDays = generateCalendarDays(currentMonth.year, currentMonth.month);
  const monthName = new Date(currentMonth.year, currentMonth.month).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const isPrevMonthDisabled = () => {
    const todayDate = new Date();
    return (
      currentMonth.year < todayDate.getFullYear() ||
      (currentMonth.year === todayDate.getFullYear() && currentMonth.month <= todayDate.getMonth())
    );
  };

  const handleOpen = () => {
    if (!isOpen) {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const GAP = 8;
        const isMobile = window.innerWidth < 400;

        if (isMobile) {
          // On very small screens, use full-width centered modal-like positioning
          const calW = Math.min(window.innerWidth - 16, CALENDAR_WIDTH);
          const leftPos = Math.max(8, (window.innerWidth - calW) / 2);
          if (spaceBelow >= CALENDAR_HEIGHT || spaceBelow >= spaceAbove) {
            setDropdownStyle({ position: "fixed", top: rect.bottom + GAP, left: leftPos, width: calW, maxHeight: spaceBelow - 16, overflowY: "auto" });
          } else {
            setDropdownStyle({ position: "fixed", bottom: window.innerHeight - rect.top + GAP, left: leftPos, width: calW, maxHeight: spaceAbove - 16, overflowY: "auto" });
          }
        } else {
          const left = Math.min(rect.left, window.innerWidth - CALENDAR_WIDTH - 8);
          if (spaceBelow >= CALENDAR_HEIGHT) {
            setDropdownStyle({ position: "fixed", top: rect.bottom + GAP, left, width: CALENDAR_WIDTH });
          } else if (spaceAbove >= CALENDAR_HEIGHT) {
            setDropdownStyle({ position: "fixed", bottom: window.innerHeight - rect.top + GAP, left, width: CALENDAR_WIDTH });
          } else {
            const useBelow = spaceBelow >= spaceAbove;
            setDropdownStyle(
              useBelow
                ? { position: "fixed", top: rect.bottom + GAP, left, width: CALENDAR_WIDTH, maxHeight: spaceBelow - 16, overflowY: "auto" }
                : { position: "fixed", bottom: window.innerHeight - rect.top + GAP, left, width: CALENDAR_WIDTH, maxHeight: spaceAbove - 16, overflowY: "auto" }
            );
          }
        }
      }

      if (!startDate) {
        setSelectionPhase("start");
      } else if (!endDate) {
        setSelectionPhase("end");
      } else {
        setSelectionPhase("start");
      }
    }
    setIsOpen(!isOpen);
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        ref={triggerRef}
        onClick={handleOpen}
        className={`flex items-center gap-3 border rounded-xl px-4 py-3.5 cursor-pointer transition-all ${isOpen
            ? "border-teal-500 ring-2 ring-teal-100"
            : "border-gray-300 hover:border-teal-400"
          }`}
      >
        <CalendarIcon />
        <div className="flex-1 flex items-center gap-2">
          <span className={`${startDate ? "text-gray-900 font-medium" : "text-gray-400"}`}>
            {startDate ? formatDate(startDate) : "Start date"}
          </span>
          <span className="text-gray-300">-</span>
          <span className={`${endDate ? "text-gray-900 font-medium" : "text-gray-400"}`}>
            {endDate ? formatDate(endDate) : "End date"}
          </span>
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {duration && (
        <div className="mt-2 flex items-center gap-2">
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
            {duration.nights} Night{duration.nights !== 1 ? "s" : ""} / {duration.days} Day
            {duration.days !== 1 ? "s" : ""}
          </span>
          {duration.days === MAX_TRIP_DAYS && (
            <span className="text-xs text-amber-600 font-medium">Maximum duration</span>
          )}
        </div>
      )}

      {isOpen && (
        <p className="mt-1.5 text-xs text-teal-600 font-medium">
          {selectionPhase === "start" ? "Select start date" : "Select end date (max 15 Nights)"}
        </p>
      )}

      {isOpen && (
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-5 z-9999" style={dropdownStyle}>
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={prevMonth}
              disabled={isPrevMonthDisabled()}
              className="p-2 hover:bg-gray-100 rounded-full disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="font-semibold text-gray-900 text-lg">{monthName}</span>
            <button
              type="button"
              onClick={nextMonth}
              className="p-2 hover:bg-gray-100 rounded-full cursor-pointer transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
              <div key={day} className="text-center text-xs font-semibold text-gray-400 py-2">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-y-1">
            {calendarDays.map((day, index) => {
              if (day === null) {
                return <div key={`empty-${index}`} className="h-10" />;
              }

              const dateStr = toISO(currentMonth.year, currentMonth.month, day);
              const isPast = isPastDate(dateStr);
              const exceedsMax = exceedsMaxRange(dateStr);
              const beforeStart = isBeforeStart(dateStr);
              const isDisabled = isPast || exceedsMax;
              const inRange = isInRange(dateStr);
              const isStart = isStartDate(dateStr);
              const isEnd = isEndDate(dateStr);
              const isHoveredEnd = isHoverEnd(dateStr);

              let bgClass = "";
              let textClass = "text-gray-700";
              let borderClass = "";
              let roundedClass = "rounded-full";

              if (isDisabled) {
                textClass = "text-gray-300";
              } else if (isStart) {
                bgClass = "bg-teal-600";
                textClass = "text-white";
                if (inRange || isEnd) {
                  roundedClass = "rounded-l-full";
                }
              } else if (isEnd && !isStart) {
                if (isHoveredEnd) {
                  borderClass = "border-2 border-dashed border-teal-500";
                  bgClass = "bg-teal-50";
                } else {
                  bgClass = "bg-teal-600";
                  textClass = "text-white";
                }
                roundedClass = "rounded-r-full";
              } else if (inRange) {
                bgClass = "bg-teal-100";
                textClass = "text-teal-800";
                roundedClass = "rounded-none";
              } else if (!isDisabled) {
                bgClass = "hover:bg-gray-100";
              }

              let wrapperClass = "";
              if (isStart && (inRange || isEnd)) {
                wrapperClass = "bg-gradient-to-r from-transparent via-teal-100 to-teal-100";
              } else if (isEnd && !isStart) {
                wrapperClass = "bg-gradient-to-r from-teal-100 via-teal-100 to-transparent";
              } else if (inRange) {
                wrapperClass = "bg-teal-100";
              }

              return (
                <div key={day} className={`relative ${wrapperClass}`}>
                  <button
                    type="button"
                    onClick={() => !isDisabled && handleDayClick(day)}
                    onMouseEnter={() => handleDayHover(day)}
                    onMouseLeave={() => setHoverDate(null)}
                    disabled={isDisabled}
                    className={`w-10 h-10 flex items-center justify-center text-sm font-medium transition-all cursor-pointer
                      ${bgClass} ${textClass} ${borderClass} ${roundedClass}
                      ${isDisabled ? "cursor-not-allowed" : ""}
                    `}
                  >
                    {day}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mt-5 pt-4 border-t border-gray-100 flex justify-between items-center">
            <button
              type="button"
              onClick={() => {
                onStartDateChange("");
                onEndDateChange("");
                setSelectionPhase("start");
                setHoverDate(null);
              }}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium cursor-pointer px-3 py-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                setHoverDate(null);
              }}
              className="text-sm text-white font-medium cursor-pointer px-4 py-1.5 bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
